"use client";

import FilterBubbles from "./FilterBubbles";
import CarsNotFound from "./cardetails/CarsNotFound";
import CarCard from "./SrpVehicleCard";
import LoadingSpinner from "@/components/common/loader/LoadingSpinner";
import { useCarData } from "@/hooks/useCarData";
import type { Car, CarDocument, CarListingResponse, CarTilesFooter, ContactDetails, FilterState, StaticTileData } from "@/types/cars";
const isDefined = <T,>(value: T | null | undefined): value is T => value != null;
const processCarDocument = (doc: CarDocument): Car | StaticTileData | null => {
  const item = doc?.document ?? null;
  if (!item) {
    return null;
  }
  if ("textAfterHighlighted" in item) {
    return item as StaticTileData;
  }
  if ("year" in item && item.year) {
    return item as Car;
  }
  return null;
};
const buildItemListName = (filters?: FilterState): string => {
  if (!filters) {
    return "search_results";
  }
  if (filters.q && filters.q.trim()) {
    return `search_results: ${filters.q.trim().slice(0, 60)}`;
  }
  const parts: string[] = [];
  if (filters.collection) parts.push(filters.collection);
  if (filters.make?.length) parts.push(filters.make.slice(0, 2).join(", "));
  if (filters.model?.length) parts.push(filters.model.slice(0, 2).join(", "));
  if (filters.bodystyle?.length) parts.push(filters.bodystyle.slice(0, 2).join(", "));
  if (filters.year) parts.push(`${filters.year[0]}-${filters.year[1]}`);
  if (filters.price) parts.push(`${filters.price[0]}-${filters.price[1]}`);
  return parts.length ? `search_results: ${parts.join(" · ")}` : "search_results";
};

// Type definitions for component props
interface CarGridProps {
  // New interface (for future use)
  cars?: CarListingResponse;
  initialCarData?: CarListingResponse;
  filters?: FilterState;
  onFiltersChange?: (filters: FilterState) => void;

  // New props for data fetching integration
  contactDetails?: ContactDetails;
  ipAddress?: string;

  // Legacy interface (for backward compatibility)
  allFacets?: unknown;
  allMakes?: unknown[];
  allModels?: unknown[];
  availableModels?: unknown[];
  updateAvailableModelsDropdown?: (makes: unknown[]) => void;
  ClearAllStates?: () => void;
  AnyFilterSelected?: () => boolean;
  minYear?: number;
  maxYear?: number;
  yearValues?: [number, number];
  setYearValues?: (values: [number, number]) => void;
  minMileage?: number;
  maxMileage?: number;
  mileageValues?: [number, number];
  setMileageValues?: (values: [number, number]) => void;
  minPrice?: number;
  maxPrice?: number;
  priceValues?: [number, number];
  setPriceValues?: (values: [number, number]) => void;
  minPayment?: number;
  maxPayment?: number;
  paymentValues?: [number, number];
  setPaymentValues?: (values: [number, number]) => void;
  bodyStyles?: string[];
  updateBodyStyles?: (bodyStyles: string[]) => void;
  interiorColors?: string[];
  exteriorColors?: string[];
  setInteriorColors?: (colors: string[]) => void;
  setExteriorColors?: (colors: string[]) => void;
  updateAvailableModels?: (models: unknown[]) => void;
  filterValues?: unknown;
  make?: unknown[];
  updateMake?: (makes: unknown[]) => void;
  model?: unknown[];
  updateModel?: (models: unknown[]) => void;
  engine?: string[];
  updateEngine?: (engines: string[]) => void;
  door?: string[];
  updateDoor?: (doors: string[]) => void;
  cylinder?: string[];
  updateCylinder?: (cylinders: string[]) => void;
  fuel?: string[];
  updateFuel?: (fuels: string[]) => void;
  transmission?: string[];
  updateTransmission?: (transmissions: string[]) => void;
  train?: string[];
  updateTrain?: (trains: string[]) => void;
  GenerateURL?: () => string;
  carTilesFooter?: CarTilesFooter;
}
const CarGrid = (props: CarGridProps) => {
  // Determine if we're using legacy props or new interface
  const isLegacyMode = Boolean(props.ClearAllStates);

  // Use new data fetching hook if not in legacy mode
  const shouldUseDataHook = !isLegacyMode;
  const carDataHook = shouldUseDataHook ? useCarData({
    contactDetails: props.contactDetails,
    ipAddress: props.ipAddress || "",
    shopOnline: false,
    // Default for now
    initialData: props.initialCarData // Pass initial data to the hook
  }) : {
    carData: null,
    isLoading: false,
    error: null
  };

  // Prioritize server-provided data to avoid hydration mismatches
  const carsData = props.initialCarData || props.cars || (isLegacyMode ? null : carDataHook.carData);
  const isLoading = props.initialCarData || props.cars ? false : isLegacyMode ? false : carDataHook.isLoading;
  const hasError = props.initialCarData || props.cars ? false : isLegacyMode ? false : Boolean(carDataHook.error);

  // Removed timestamp calculations to avoid hydration mismatches

  const processedCarItems = (carsData?.items ?? []).map(processCarDocument).filter(isDefined);
  const itemListName = buildItemListName(props.filters);
  const listPage = carsData?.pagination?.page || 1;
  const listSort = props.filters?.sort || "specials";

  // Removed localStorage-based navigation - now handled via URL state

  return <div className="t-bg-transparent">
            {/* Filter Bubbles - Legacy compatibility */}
            {isLegacyMode && <FilterBubbles updateAvailableModelsDropdown={props.updateAvailableModelsDropdown} ClearAllStates={props.ClearAllStates} AnyFilterSelected={props.AnyFilterSelected} allMakes={props.allMakes} minYear={props.minYear} maxYear={props.maxYear} yearValues={props.yearValues} setYearValues={props.setYearValues} minMileage={props.minMileage} maxMileage={props.maxMileage} mileageValues={props.mileageValues} setMileageValues={props.setMileageValues} minPrice={props.minPrice} maxPrice={props.maxPrice} priceValues={props.priceValues} minPayment={props.minPayment} maxPayment={props.maxPayment} paymentValues={props.paymentValues} setPriceValues={props.setPriceValues} setPaymentValues={props.setPaymentValues} bodyStyles={props.bodyStyles} updateBodyStyles={props.updateBodyStyles} interiorColors={props.interiorColors} exteriorColors={props.exteriorColors} setInteriorColors={props.setInteriorColors} setExteriorColors={props.setExteriorColors} allModels={props.allModels} availableModels={props.availableModels} updateAvailableModels={props.updateAvailableModels} filterValues={props.filterValues} allFacets={props.allFacets} make={props.make} updateMake={props.updateMake} model={props.model} updateModel={props.updateModel} engine={props.engine} updateEngine={props.updateEngine} door={props.door} updateDoor={props.updateDoor} cylinder={props.cylinder} updateCylinder={props.updateCylinder} fuel={props.fuel} updateFuel={props.updateFuel} transmission={props.transmission} updateTransmission={props.updateTransmission} train={props.train} updateTrain={props.updateTrain} />}

            {/* Loading state */}
            {isLoading && <div className="t-flex t-justify-center t-items-center t-min-h-96">
                    <LoadingSpinner />
                </div>}

            {/* Error state */}
            {hasError && <CarsNotFound ErrorMessage="Error loading cars. Please try again." />}

            {/* No cars found */}
            {!isLoading && !hasError && carsData != null && carsData?.items?.length === 0 && <CarsNotFound ErrorMessage="Nothing was found for your query" />}

            {/* Car grid */}
            {!isLoading && !hasError && carsData != null && carsData?.items?.length > 0 && <ol className="t-grid t-w-full t-auto-rows-fr t-grid-cols-1 t-gap-5 md:t-grid-cols-2 xl:t-grid-cols-3 xl:t-gap-6" role="list" aria-label="Car listings">
                    {processedCarItems.map((item: Car | StaticTileData, index: number) => {
        return <li key={"vin" in item ? item.vin : `static-${index}`} className="t-list-none t-min-w-0">
                                <CarCard car={item} itemListName={itemListName} listPage={listPage} listSort={listSort} priority={index < 3} />
                            </li>;
      })}
                </ol>}
        </div>;
};
export default CarGrid;